import type { RouteLocation, RouteLocationRaw } from 'vue-router'

export interface IMegaMenuItem {
	title?: string
	translation_key_title?: string
	link?: string | RouteLocation | RouteLocationRaw
	subText?: string
	translation_key_subText?: string
	icon?: string
	menu?: boolean
	filters?: string[]
	items?: IMegaMenuItem[]
	location?: string // for gtm
	rental_length?: string // for gtm
	category?: string // for gtm
}

export const nav = [
	{ translation_key_label: 'cars', to: 'cars', type: 'link' },
	{
		translation_key_label: 'offer',
		to: '#',
		type: 'dropdown',
	},
	{ translation_key_label: 'locations', to: 'locations', type: 'link' },
	{ translation_key_label: 'frequent_questions', to: 'https://intercom-help.eu/carfree/pl/', type: 'link', rel: 'nofollow', newTab: true },
	{ translation_key_label: 'contact', to: 'contact', type: 'link' },
	{ translation_key_label: 'app', to: 'https://carfree.webflow.io/', type: 'link', newTab: true },
	{ translation_key_label: 'news', to: 'news-page', type: 'link', showOnlyOnPL: true },
]

export const megaMenuData: IMegaMenuItem[] = [
	{
		translation_key_title: 'type_of_rental',
		items: [
			{
				translation_key_title: 'short_term_rentals',
				translation_key_subText: 'for_days',
				icon: 'calendar',
				link: 'short-term',
				rental_length: 'short_term',
			},
			{
				translation_key_title: 'medium_term_rental',
				translation_key_subText: 'for_months',
				icon: 'calendar',
				link: 'medium-term',
				rental_length: 'medium_term',
				items: [
					{
						translation_key_title: 'rental_for_a_month',
						link: 'month',
					},
					{
						translation_key_title: 'rental_for_2_months',
						link: '2months',
					},
					{
						translation_key_title: 'rental_for_3_months',
						link: '3months',
					},
					{
						translation_key_title: 'rental_for_6_months',
						link: '6months',
					},
				],
			},
			{
				translation_key_title: 'long_term_rental',
				translation_key_subText: 'for_years',
				icon: 'calendar',
				link: 'long-term',
				rental_length: 'long_term',
			},
		],
	},
	{
		translation_key_title: 'categories_of_vehicles',
		items: [
			{
				translation_key_title: 'electric_car_rental',
				icon: 'battery-outlined',
				filters: [EGroupName.ELECTRIC],
				link: 'electric-cars',
				category: 'electric',
				items: [
					{
						translation_key_title: 'tesla_rental',
						link: 'rent-tesla',
					},
				],
			},
			{
				translation_key_title: 'bus_rental',
				icon: 'bus',
				link: 'bus-rental',
				filters: [EGroupName.VAN],
				category: 'bus',
				items: [
					{
						translation_key_title: 'rental_6_passenger_bus',
						link: '6bus',
					},
					{
						translation_key_title: 'rental_7_passenger_bus',
						link: '7bus',
					},
					{
						translation_key_title: 'rental_8_passenger_bus',
						link: '8bus',
					},
					{
						translation_key_title: 'rental_9_passenger_bus',
						link: '9bus',
					},
				],
			},
			{
				translation_key_title: 'delivery_van_rental',
				icon: 'delivery-bus',
				filters: [EGroupName.DELIVERY],
				link: 'delivery-bus-rental',
				category: 'delivery',
				items: [
					{
						translation_key_title: 'rental_refrigeration_van',
						link: 'refrigeration-van-rental',
					},
				],
			},
		],
	},
	{
		translation_key_title: 'locations',
		items: [
			{
				translation_key_title: 'cities',
				icon: 'location',
				link: 'locations',
				menu: true,
				category: 'city',
			},
			{
				translation_key_title: 'airports',
				icon: 'airplane-outline',
				link: 'locations',
				menu: true,
				category: 'airport',
			},
		],
	},
]

// google place ids to sort menu items (taken from admin panel)
// showInMenu must be set to true in admin panel in order to show in menu (panel/locations)
export const busesIDs = [
	{
		id: 'ChIJLfANzhtbFkcR0DhjZt2ji74',
		translation_key_title: 'bus_krakow',
	},
	{
		id: 'ChIJoXlmUXZz_UYR-GuY-3NIw_o',
		translation_key_title: 'bus_gdansk',
	},
	{
		id: 'ChIJIdU9c2XCD0cRr2plzRHgRLQ',
		translation_key_title: 'bus_wroclaw',
	},
	{
		id: 'ChIJDd6RQTJbBEcRjeoD5weQIW0',
		translation_key_title: 'bus_poznan',
	},
	{
		id: 'ChIJU_UibVsqF0cRcTq62XniNH0',
		translation_key_title: 'bus_katowice',
	},
	{
		id: 'ChIJbW_8G-ATA0cR_wBVD_aKDxY',
		translation_key_title: 'bus_bydgoszcz',
	},
	{
		id: 'ChIJLWp6qw5XIkcRrA8_szkh3hk',
		translation_key_title: 'bus_lublin',
	},
	{
		id: 'ChIJ9xedsC7LG0cRqi5W8Lof38A',
		translation_key_title: 'bus_lodz',
	},
]

export const deliveryBusesIDs = [
	{
		id: 'ChIJm7fQXhsJqkcR7uQEoC4USr8',
		translation_key_title: 'delivery_bus_szczecin',
	},
	{
		id: 'ChIJS-bNCzJbBEcRgdBNLLNpIFo',
		translation_key_title: 'delivery_bus_poznan',
	},
	{
		id: 'ChIJLfANzhtbFkcR0DhjZt2ji74',
		translation_key_title: 'delivery_bus_krakow',
	},
	{
		id: 'ChIJIdU9c2XCD0cRr2plzRHgRLQ',
		translation_key_title: 'delivery_bus_wroclaw',
	},
	{
		id: 'ChIJy97ZZKXPFkcRa6jqKE3iVvE',
		translation_key_title: 'delivery_bus_katowice',
	},
	{
		id: 'ChIJbW_8G-ATA0cR_wBVD_aKDxY',
		translation_key_title: 'delivery_bus_bydgoszcz',
	},
	{
		id: 'ChIJB3XnmR41GkcRDFlbjK09WJ0',
		translation_key_title: 'delivery_bus_lodz',
	},
	{
		id: 'ChIJlU8GFR37PEcRnIbNkdZ1Cs8',
		translation_key_title: 'delivery_bus_rzeszow',
	},
	{
		id: 'ChIJsUikI339H0cRFhFGTHOEWiY',
		translation_key_title: 'delivery_bus_bialystok',
	},
]

export const branchesIDs = [
	{
		id: 'ChIJwaQscSAzGUcRuLGV50QDczE',
		translation_key_title: 'car_rental_warszawa',
	},
	{
		id: 'ChIJUdk3CxpbFkcRkuq4Azj30FE',
		translation_key_title: 'car_rental_krakow',
	},
	{
		id: 'ChIJpSaLZM1z_UYRQPhiB-OeGas',
		translation_key_title: 'car_rental_gdansk',
	},
	{
		id: 'ChIJ-4nbblTDD0cROUaetd4mRF8',
		translation_key_title: 'car_rental_wroclaw',
	},
	{
		id: 'ChIJd5jGjfpbBEcRk314jZSV1_o',
		translation_key_title: 'car_rental_poznan',
	},
	{
		id: 'ChIJZdR7kUfOFkcR1I8wvOUCUAg',
		translation_key_title: 'car_rental_katowice',
	},
	{
		id: 'ChIJmT4h2ssTA0cRKKIIztfZrec',
		translation_key_title: 'car_rental_bydgoszcz',
	},
	{
		id: 'ChIJq95X42hXIkcRpccA4G5umh0',
		translation_key_title: 'car_rental_lublin',
	},
	{
		id: 'ChIJ9xedsC7LG0cRlZuZZ6y4BfE',
		translation_key_title: 'car_rental_lodz',
	},
	{
		id: 'ChIJlU8GFR37PEcRnIbNkdZ1Cs8',
		translation_key_title: 'car_rental_rzeszow',
	},
]

export const airportsIDs = [
	{
		id: 'ChIJ41zpQhwzGUcR0ehDaKL1xSI',
		translation_key_title: 'car_rental_warszawa_airport',
	},
	{
		id: 'ChIJZ4xL7SdZFkcR1Mwtra0t3j0',
		translation_key_title: 'car_rental_krakow_airport',
	},
	{
		id: 'ChIJ8eQhiTSe_UYR-JUf3ZO59IM',
		translation_key_title: 'car_rental_gdansk_airport',
	},
	{
		id: 'ChIJv11892_AD0cRf27AnQ6i4CQ',
		translation_key_title: 'car_rental_wroclaw_airport',
	},
	{
		id: 'ChIJneKyVa5FBEcRfWWTxgwX7UM',
		translation_key_title: 'car_rental_poznan_airport',
	},
	{
		id: 'ChIJwwUsj0GlHkcRYXefnNYCGHs',
		translation_key_title: 'car_rental_modlin_airport',
	},
	{
		id: 'ChIJLb2T-1sqF0cR3zFjIxMOr7w',
		translation_key_title: 'car_rental_katowice_airport',
	},
	{
		id: 'ChIJH2sQUhcTA0cRFGBrNh3I2YQ',
		translation_key_title: 'car_rental_bydgoszcz_airport',
	},
	{
		id: 'ChIJ9V9pp6I1GkcRiWYwtqaBvMU',
		translation_key_title: 'car_rental_lodz_airport',
	},
	{
		id: 'ChIJR74qznZUIkcRuEwWIkG2csk',
		translation_key_title: 'car_rental_lublin_airport',
	},
]

// TEMP solution to not show pages with no eng version
export const megaMenuDataEN: IMegaMenuItem[] = [
	{
		translation_key_title: 'type_of_rental',
		items: [
			{
				translation_key_title: 'short_term_rentals',
				translation_key_subText: 'for_days',
				icon: 'calendar',
				link: 'short-term',
				rental_length: 'short_term',
			},
			{
				translation_key_title: 'medium_term_rental',
				translation_key_subText: 'for_months',
				icon: 'calendar',
				link: 'medium-term',
				rental_length: 'medium_term',
				items: [
					{
						translation_key_title: 'rental_for_a_month',
						link: 'month',
					},
					{
						translation_key_title: 'rental_for_2_months',
						link: '2months',
					},
					{
						translation_key_title: 'rental_for_3_months',
						link: '3months',
					},
					{
						translation_key_title: 'rental_for_6_months',
						link: '6months',
					},
				],
			},
			{
				translation_key_title: 'long_term_rental',
				translation_key_subText: 'for_years',
				icon: 'calendar',
				link: 'long-term',
				rental_length: 'long_term',
			},
		],
	},
	{
		translation_key_title: 'categories_of_vehicles',
		items: [
			{
				translation_key_title: 'electric_car_rental',
				icon: 'battery-outlined',
				filters: [EGroupName.ELECTRIC],
				link: 'electric-cars',
				category: 'electric',
				items: [
					{
						translation_key_title: 'tesla_rental',
						link: 'rent-tesla',
					},
				],
			},
			{
				translation_key_title: 'bus_rental',
				icon: 'bus',
				link: 'bus-rental',
				filters: [EGroupName.VAN],
				category: 'bus',
				items: [
					{
						translation_key_title: 'rental_6_passenger_bus',
						link: '6bus',
					},
					{
						translation_key_title: 'rental_7_passenger_bus',
						link: '7bus',
					},
					{
						translation_key_title: 'rental_8_passenger_bus',
						link: '8bus',
					},
					{
						translation_key_title: 'rental_9_passenger_bus',
						link: '9bus',
					},
				],
			},
			{
				translation_key_title: 'delivery_van_rental',
				icon: 'delivery-bus',
				filters: [EGroupName.DELIVERY],
				link: 'delivery-bus-rental',
				category: 'delivery',
				items: [
					{
						translation_key_title: 'rental_refrigeration_van',
						link: 'refrigeration-van-rental',
					},
				],
			},
		],
	},
	{
		translation_key_title: 'locations',
		items: [
			{
				translation_key_title: 'cities',
				icon: 'location',
				link: 'locations',
				category: 'city',
			},
			{
				translation_key_title: 'airports',
				icon: 'airplane-outline',
				link: 'locations',
				menu: true,
				category: 'airport',
			},
		],
	},
]

export const busesIDsEN = []
export const deliveryBusesIDsEN = []
export const branchesIDsEN = []
export const airportsIDsEN = [
	{
		id: 'ChIJ41zpQhwzGUcR0ehDaKL1xSI',
		translation_key_title: 'car_rental_warszawa_airport',
	},
	{
		id: 'ChIJZ4xL7SdZFkcR1Mwtra0t3j0',
		translation_key_title: 'car_rental_krakow_airport',
	},
	{
		id: 'ChIJ8eQhiTSe_UYR-JUf3ZO59IM',
		translation_key_title: 'car_rental_gdansk_airport',
	},
	{
		id: 'ChIJv11892_AD0cRf27AnQ6i4CQ',
		translation_key_title: 'car_rental_wroclaw_airport',
	},
	{
		id: 'ChIJwwUsj0GlHkcRYXefnNYCGHs',
		translation_key_title: 'car_rental_modlin_airport',
	},
	{
		id: 'ChIJLb2T-1sqF0cR3zFjIxMOr7w',
		translation_key_title: 'car_rental_katowice_airport',
	},
]
